import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { getApi } from "@/api";
import { markReportPaidMock } from "@/api/mockClient";
import { setSessionId, track } from "@/analytics/analytics";
import { ExternalLink } from "@/components/ExternalLink";
import { PageEstimateHint } from "@/components/PageEstimateHint";
import { SupportLink } from "@/components/SupportLink";
import { siteLinks } from "@/config/branding";
import { env } from "@/config/env";
import { startCheckout } from "@/features/checkout/checkout";
import { normalizeArtifactDownloadUrl } from "@/utils/downloadUrls";
import { approxPages } from "@/utils/documentSize";
import { formatDeadline, formatPrice } from "@/utils/formatPrice";
import { NextPhaseHandoff } from "@/components/NextPhaseHandoff";
import { useReportStatus } from "./useReportStatus";
import type { PreviewItem, PreviewSection, PricingBreakdown, StatusResponse } from "@/types/api";

interface StatusScreenProps {
  /** Explicit report id (embedded usage). Falls back to the route param when omitted. */
  reportId?: string;
  /** Explicit offer id (embedded usage). Falls back to route/query/default when omitted. */
  offerId?: string;
  /** Rendered inline inside the embed widget rather than as a standalone page. */
  embedded?: boolean;
  /** Absolute app origin for Stripe return URLs when embedded on an external host page. */
  appBaseUrl?: string;
}

/**
 * Shared status + preview view. Used as the standalone landing page (emails link here) and,
 * when given explicit props, inline inside the embed so embedded users see live progress,
 * the preview, and the unlock CTA without leaving the host page.
 */
export function StatusScreen({
  reportId: reportIdProp,
  offerId: offerIdProp,
  embedded = false,
  appBaseUrl,
}: StatusScreenProps = {}) {
  const params = useParams();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const reportId = reportIdProp ?? params.reportId;
  const offerId =
    offerIdProp ?? params.offerId ?? search.get("offerId") ?? env.defaultOfferId ?? "sop-coverage";
  const stripeStorageKey = reportId ? `r1-stripe-session:${reportId}` : null;
  const stripeSessionId =
    search.get("session_id") ??
    (stripeStorageKey ? sessionStorage.getItem(stripeStorageKey) : null);
  const confirmStartedRef = useRef(false);

  // Poll until paid after Stripe return (session_id) or while embedded (user may still be in checkout).
  const pollUntilPaid = embedded || !!stripeSessionId;
  const { data, loading, error } = useReportStatus(reportId, pollUntilPaid ? { pollUntilPaid: true } : undefined);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [redirecting, setRedirecting] = useState(false);
  const [confirmedDownloadUrl, setConfirmedDownloadUrl] = useState<string | null>(null);
  const [confirmedExcelUrl, setConfirmedExcelUrl] = useState<string | null>(null);

  useEffect(() => {
    if (reportId) setSessionId(reportId);
  }, [reportId]);

  useEffect(() => {
    const fromUrl = search.get("session_id");
    if (fromUrl && stripeStorageKey) sessionStorage.setItem(stripeStorageKey, fromUrl);
  }, [search, stripeStorageKey]);

  useEffect(() => {
    if (!stripeSessionId || !reportId || confirmStartedRef.current) return;
    confirmStartedRef.current = true;

    track("payment_success_returned", { reportId, offerId });
    if (env.useMockApi) {
      markReportPaidMock(reportId);
      if (stripeStorageKey) sessionStorage.removeItem(stripeStorageKey);
      if (search.get("session_id")) navigate(`/${offerId}/preview/${reportId}`, { replace: true });
      return;
    }

    const api = getApi();
    void api
      .confirmStripeSession({ sessionId: stripeSessionId })
      .then((res) => {
        if (res.fullDownloadUrl) setConfirmedDownloadUrl(res.fullDownloadUrl);
        if (res.fullExcelDownloadUrl) setConfirmedExcelUrl(res.fullExcelDownloadUrl);
      })
      .catch(() => {
        /* status polling will unlock once webhook/confirm succeeds */
      });

    if (search.get("session_id")) navigate(`/${offerId}/preview/${reportId}`, { replace: true });
  }, [stripeSessionId, reportId, offerId, navigate, search, stripeStorageKey]);

  const unlocked = data?.paymentState === "paid" || data?.status === "paid";
  const confirmingPayment = !!stripeSessionId && !unlocked;

  useEffect(() => {
    if (unlocked && stripeStorageKey) sessionStorage.removeItem(stripeStorageKey);
  }, [unlocked, stripeStorageKey]);
  const fullDownloadUrl = reportId
    ? normalizeArtifactDownloadUrl(
        data?.fullDownloadUrl ?? confirmedDownloadUrl ?? undefined,
        reportId,
        "word",
      )
    : undefined;
  const fullExcelDownloadUrl = reportId
    ? normalizeArtifactDownloadUrl(
        data?.fullExcelDownloadUrl ?? confirmedExcelUrl ?? undefined,
        reportId,
        "excel",
      )
    : undefined;
  const previewDownloadUrl = reportId
    ? normalizeArtifactDownloadUrl(data?.previewDownloadUrl, reportId, "preview")
    : undefined;
  const previewExcelDownloadUrl = reportId
    ? normalizeArtifactDownloadUrl(data?.previewExcelDownloadUrl, reportId, "preview-excel")
    : undefined;
  const sourceTextViewUrl = data?.sourceTextViewUrl;
  const paymentPending = data?.paymentState === "pending";
  // Zero findings detected: surface a clear "nothing to unlock" notice instead of the paywall.
  const showEmptyState = !!data?.noFindings && !unlocked;
  // Coverage runs compare a regulation to SOPs; obligations runs extract from one document.
  // Used to keep empty-state copy accurate for each product.
  const resolvedOfferId = data?.offerId ?? offerId;
  const isCoverage = resolvedOfferId === "sop-coverage";

  const onUnlock = async () => {
    if (!reportId || unlocked) return;
    if (paymentPending && data?.checkoutUrl) {
      window.location.href = data.checkoutUrl;
      return;
    }
    setCheckoutError(null);
    setRedirecting(true);
    try {
      const returnOrigin =
        embedded && appBaseUrl ? appBaseUrl : window.location.origin;
      await startCheckout(reportId, offerId, returnOrigin);
    } catch (e) {
      setCheckoutError((e as Error).message);
      setRedirecting(false);
    }
  };

  const priceLabel =
    data?.pricingBreakdown?.priceCents != null
      ? formatPrice(data.pricingBreakdown.priceCents, data.quotedCurrency ?? "eur")
      : data?.quotedPriceCents != null
        ? formatPrice(data.quotedPriceCents, data.quotedCurrency ?? "eur")
        : null;

  return (
    <div className={`mx-auto w-full max-w-2xl px-5 ${embedded ? "py-8" : "flex-1 py-12"}`}>
      {loading && !data ? (
        <Waiting message="Checking your report status…" />
      ) : null}

      {data?.status === "processing" ? (
        <Waiting data={data} message={data.possiblyStuck ? data.message : undefined} />
      ) : null}

      {confirmingPayment ? (
        <div className="mb-6 rounded-2xl border border-brand-200 bg-brand-50 p-4 text-brand-900">
          <p className="font-semibold">Confirming your payment…</p>
          <p className="mt-1 text-sm text-brand-800">
            This usually takes a few seconds. Your downloads will appear below once confirmed.
          </p>
        </div>
      ) : null}

      {data && (data.status === "preview_ready" || data.status === "paid") ? (
        <div className="animate-fade-in">
          <span
            className={`inline-flex rounded-full px-3 py-1 text-sm font-semibold ${
              showEmptyState
                ? "bg-amber-100 text-amber-700"
                : unlocked
                  ? "bg-green-100 text-green-700"
                  : confirmingPayment
                    ? "bg-brand-100 text-brand-800"
                    : "bg-green-100 text-green-700"
            }`}
          >
            {unlocked
              ? isCoverage
                ? "Full report ready"
                : "Full register ready"
              : confirmingPayment
                ? "Confirming payment"
                : showEmptyState
                  ? "No purchase needed"
                  : "Preview ready"}
          </span>
          <h1 className="mt-4 text-2xl font-bold text-ink sm:text-3xl">
            {unlocked
              ? "Your report is ready"
              : confirmingPayment
                ? "Almost there"
                : showEmptyState
                  ? "Analysis complete"
                  : (data.preview?.title ?? "Your report preview")}
          </h1>
          {unlocked ? (
            <p className="mt-3 text-lg text-ink-soft">
              Payment confirmed. Download your full Word report and Excel register below. We've also
              emailed you the links.
            </p>
          ) : confirmingPayment ? (
            <p className="mt-3 text-lg text-ink-soft">
              Payment received. We're preparing your full Word report and Excel register.
            </p>
          ) : data.preview?.summary ? (
            <p className="mt-3 text-lg text-ink-soft">{data.preview.summary}</p>
          ) : null}

          {showEmptyState ? (
            <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-5 text-amber-900">
              <p className="font-semibold">
                {isCoverage
                  ? "No assessable obligations found, so you won't be charged."
                  : "No obligations detected, so you won't be charged."}
              </p>
              <p className="mt-2 text-sm">
                {isCoverage ? (
                  <>
                    We couldn't find any assessable obligations in the regulation to compare against your
                    SOPs. This typically happens when the first file isn't a regulation/standard, the
                    document is purely informational, it's a scanned image without selectable text, or
                    the upload order was reversed. Check that the regulation was uploaded first, try
                    again, or <SupportLink className="font-medium underline" /> if you believe this is an
                    error.
                  </>
                ) : (
                  <>
                    We couldn't find any obligations to include in your deliverable. This typically
                    happens when the document is purely informational, is a scanned image without
                    selectable text, or the wrong file was uploaded. Try submitting a different
                    document, or <SupportLink className="font-medium underline" /> if you believe this is
                    an error.
                  </>
                )}
              </p>
            </div>
          ) : null}

          {showEmptyState ? null : unlocked ? (
            <div className="mt-8 space-y-4">
              <div className="flex flex-wrap gap-3">
                {fullDownloadUrl ? (
                  <a className="r1-btn-primary inline-flex" href={fullDownloadUrl} download>
                    <DownloadIcon />
                    Download Word report
                  </a>
                ) : null}
                {fullExcelDownloadUrl ? (
                  <a className="r1-btn-outline inline-flex" href={fullExcelDownloadUrl} download>
                    <DownloadIcon />
                    Download Excel register
                  </a>
                ) : null}
              </div>
              <PreviewTeaser
                data={data}
                previewDownloadUrl={previewDownloadUrl}
                previewExcelDownloadUrl={previewExcelDownloadUrl}
                sourceTextViewUrl={sourceTextViewUrl}
                defaultCollapsed
              />
            </div>
          ) : (
            <PreviewTeaser
              data={data}
              previewDownloadUrl={previewDownloadUrl}
              previewExcelDownloadUrl={previewExcelDownloadUrl}
              sourceTextViewUrl={sourceTextViewUrl}
            />
          )}

          {showEmptyState ? null : !unlocked ? (
            <div className="mt-8 rounded-2xl border border-slate-200 bg-slate-50 p-5">
              {priceLabel ? (
                <div className="space-y-2">
                  <p className="text-lg font-semibold text-ink">
                    {isCoverage
                      ? `Upgrade to the full report for ${priceLabel} excl. VAT (Word + Excel)`
                      : `Upgrade to the full register for ${priceLabel} excl. VAT (Word + Excel)`}
                  </p>
                  <BuyerStats data={data} />
                  <PricingDetails data={data} />
                </div>
              ) : null}
              <button
                type="button"
                className="r1-btn-primary mt-4 text-lg"
                onClick={() => void onUnlock()}
                disabled={redirecting}
              >
                {redirecting
                  ? "Redirecting…"
                  : paymentPending
                    ? "Resume checkout"
                    : isCoverage
                      ? "Upgrade to full report"
                      : "Upgrade to full register"}
              </button>
              {paymentPending ? (
                <p className="mt-2 text-sm text-ink-muted">
                  Checkout was started but not completed. Resume to finish payment, or wait a moment if
                  you already paid.
                </p>
              ) : embedded ? (
                <p className="mt-2 text-xs text-ink-muted">
                  Secure checkout via Stripe. After payment you'll return to the full report page with
                  your downloads.
                </p>
              ) : null}
              {data.preview?.expiresAt ? (
                <p className="mt-2 text-xs text-ink-muted">
                  Preview available until {formatDeadline(data.preview.expiresAt)}
                </p>
              ) : null}
              {checkoutError ? (
                <p role="alert" className="mt-2 text-sm font-medium text-red-600">
                  {checkoutError}
                </p>
              ) : null}
              <p className="mt-4 border-t border-slate-200 pt-3 text-sm text-ink-soft">
                {isCoverage
                  ? "These are real gaps from your documents. The full report includes every finding with filterable results, coverage status, each SOP candidate and its scores, what contributed, reviewers' decision, owner, and due-date columns to drive the compliance effort."
                  : "These are real obligations from your document. The full register includes every item decomposed with stable IDs, ready for your reviewers to work through and file."}
              </p>
              <p className="mt-3 text-xs text-ink-muted">
                Running many analyses?{" "}
                <ExternalLink
                  href={siteLinks.contact}
                  className="font-medium text-brand-600 underline"
                >
                  Contact us about volume pricing
                </ExternalLink>
                .
              </p>
            </div>
          ) : null}

          {showEmptyState ? null : <NextPhaseHandoff offerId={resolvedOfferId} />}
        </div>
      ) : null}

      {data?.status === "failed" ? (
        <div className="animate-fade-in rounded-2xl border border-red-200 bg-red-50 p-6 text-red-800">
          <h1 className="text-xl font-bold">Something went wrong</h1>
          <p className="mt-2">
            We couldn't generate your report. Please try submitting again, or{" "}
            <SupportLink className="font-medium underline" /> if the problem persists.
          </p>
          {data.message ? (
            <p className="mt-3 rounded-lg bg-red-100/80 px-3 py-2 text-sm font-mono">{data.message}</p>
          ) : null}
        </div>
      ) : null}

      {error && !data ? (
        <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-4 text-amber-800">
          {error}
        </div>
      ) : null}
    </div>
  );
}

function DownloadIcon() {
  return (
    <svg
      className="h-5 w-5 shrink-0"
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M10 2a.75.75 0 0 1 .75.75v7.69l2.22-2.22a.75.75 0 1 1 1.06 1.06l-3.5 3.5a.75.75 0 0 1-1.06 0l-3.5-3.5a.75.75 0 1 1 1.06-1.06l2.22 2.22V2.75A.75.75 0 0 1 10 2Z" />
      <path d="M3.5 12.75a.75.75 0 0 1 .75.75v1.5A2.25 2.25 0 0 0 6.5 17.25h7a2.25 2.25 0 0 0 2.25-2.25v-1.5a.75.75 0 0 1 1.5 0v1.5A3.75 3.75 0 0 1 13.5 18.75h-7A3.75 3.75 0 0 1 2.75 15v-1.5a.75.75 0 0 1 .75-.75Z" />
    </svg>
  );
}

/** Preview highlights, findings, and sample download (expanded before purchase, collapsed after). */
function PreviewTeaser({
  data,
  previewDownloadUrl,
  previewExcelDownloadUrl,
  sourceTextViewUrl,
  defaultCollapsed = false,
}: {
  data: StatusResponse;
  previewDownloadUrl?: string;
  previewExcelDownloadUrl?: string;
  sourceTextViewUrl?: string;
  defaultCollapsed?: boolean;
}) {
  const preview = data.preview;
  const hasHighlights = (preview?.highlights?.length ?? 0) > 0;
  const hasFindings =
    (preview?.sections?.length ?? 0) > 0 || (preview?.items?.length ?? 0) > 0;
  const hasPreviewDoc = !!previewDownloadUrl || !!previewExcelDownloadUrl;
  const hasSourceTextView = !!sourceTextViewUrl;
  const hasExpiry = !!preview?.expiresAt && !defaultCollapsed;

  if (!hasHighlights && !hasFindings && !hasPreviewDoc && !hasSourceTextView && !hasExpiry) return null;

  const summaryLabel = previewSampleSummary(preview);

  const body = (
    <>
      {hasExpiry && preview?.expiresAt ? (
        <p className="mt-2 text-sm text-ink-muted">
          Preview available until {formatDeadline(preview.expiresAt)}
        </p>
      ) : null}

      {hasHighlights && preview?.highlights ? (
        <ul className="mt-4 flex flex-col gap-2">
          {preview.highlights.map((h, i) => (
            <li key={i} className="flex items-start gap-2 text-ink">
              <span className="mt-1 text-brand-600" aria-hidden="true">
                ✓
              </span>
              <span>{h}</span>
            </li>
          ))}
        </ul>
      ) : null}

      {hasFindings ? <PreviewFindings preview={preview} /> : null}

      {hasPreviewDoc ? (
        <div className="mt-6 rounded-2xl border border-brand-200 bg-brand-50/60 p-4">
          <p className="text-sm font-medium text-ink">Your free sample is ready</p>
          <div className="mt-3 flex flex-wrap gap-3">
            {previewDownloadUrl ? (
              <a className="r1-btn-outline inline-flex" href={previewDownloadUrl} download>
                <DownloadIcon />
                Download preview (Word)
              </a>
            ) : null}
            {previewExcelDownloadUrl ? (
              <a className="r1-btn-outline inline-flex" href={previewExcelDownloadUrl} download>
                <DownloadIcon />
                Download preview (Excel)
              </a>
            ) : null}
          </div>
          <p className="mt-2 text-xs text-ink-muted">
            {previewDownloadUrl && previewExcelDownloadUrl
              ? "Click to download your sample Word or Excel file"
              : previewExcelDownloadUrl
                ? "Click to download your sample Excel file"
                : "Click to download your sample Word file"}
          </p>
        </div>
      ) : null}

      {hasSourceTextView && sourceTextViewUrl ? (
        <p className="mt-4 text-sm">
          <a
            className="font-medium text-brand-700 underline underline-offset-2 hover:text-brand-900"
            href={sourceTextViewUrl}
            target="_blank"
            rel="noreferrer"
          >
            View source text
          </a>
          <span className="text-ink-muted">
            {" "}
            (spike: full primary document with block anchors; append #BlockId to jump)
          </span>
        </p>
      ) : null}
    </>
  );

  if (defaultCollapsed) {
    return (
      <details className="mt-6 rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
        <summary className="cursor-pointer select-none text-sm font-semibold text-ink-soft">
          {summaryLabel}
        </summary>
        <div className="mt-3">{body}</div>
      </details>
    );
  }

  return <div className="mt-6">{body}</div>;
}

function previewSampleShownCount(preview?: StatusResponse["preview"]): number {
  return (
    preview?.items?.length ??
    preview?.sections?.reduce((acc, s) => acc + (s.items?.length ?? 0), 0) ??
    0
  );
}

function previewSampleCountLabel(preview?: StatusResponse["preview"]): string {
  const shown = previewSampleShownCount(preview);
  const total = preview?.totalItems;

  if (total != null && total > 0 && shown > 0 && total > shown) {
    return ` (${shown.toLocaleString()} of ${total.toLocaleString()})`;
  }
  if (total != null && total > 0) {
    return ` (${total.toLocaleString()} finding${total === 1 ? "" : "s"})`;
  }
  if (shown > 0) {
    return ` (${shown.toLocaleString()} shown)`;
  }
  return "";
}

function previewSampleSummary(preview?: StatusResponse["preview"]): string {
  const suffix = previewSampleCountLabel(preview);
  return suffix ? `Preview sample${suffix}` : "Preview sample";
}

/** Buyer-native stats line: gaps · findings · documents. */
function BuyerStats({ data }: { data: StatusResponse }) {
  const parts: string[] = [];
  if (data.gapCount != null) parts.push(`${data.gapCount.toLocaleString()} gap${data.gapCount === 1 ? "" : "s"}`);
  if (data.findingCount != null)
    parts.push(`${data.findingCount.toLocaleString()} finding${data.findingCount === 1 ? "" : "s"}`);
  if (data.documentCount != null)
    parts.push(`${data.documentCount.toLocaleString()} document${data.documentCount === 1 ? "" : "s"}`);

  if (parts.length === 0) return null;
  return <p className="text-sm font-medium text-ink-soft">{parts.join(" · ")}</p>;
}

function formatPricingBreakdownLine(
  breakdown: PricingBreakdown,
  currency: string,
  billableCharacters?: number,
): string {
  const base = formatPrice(breakdown.baseComponentCents, currency);
  const volume = formatPrice(breakdown.volumeComponentCents, currency);
  const computed =
    breakdown.computedCents != null
      ? formatPrice(breakdown.computedCents, currency)
      : formatPrice(breakdown.baseComponentCents + breakdown.volumeComponentCents, currency);
  const charged =
    breakdown.priceCents != null
      ? formatPrice(breakdown.priceCents, currency)
      : computed;

  if (!breakdown.minimumApplied) {
    return `Base ${base} + volume ${volume} = ${charged}.`;
  }

  if ((billableCharacters ?? 0) <= 0) {
    return `Minimum price ${charged} applies (document within the included character tier).`;
  }

  return `Base ${base} + volume ${volume} = ${computed}; minimum price ${charged} applies.`;
}

/** Collapsible character-based pricing detail (secondary to buyer-native stats). */
function PricingDetails({ data }: { data: StatusResponse }) {
  if (data.totalCharacters == null) return null;

  const breakdown = data.pricingBreakdown;
  const pages = approxPages(data.totalCharacters);
  return (
    <details className="text-sm text-ink-muted">
      <summary className="cursor-pointer select-none font-medium text-ink-soft">Pricing details</summary>
      <div className="mt-2 space-y-1">
        <p>
          Quote based on{" "}
          <PageEstimateHint characters={data.totalCharacters}>
            about {pages.toLocaleString()} page{pages === 1 ? "" : "s"}
          </PageEstimateHint>
          {data.documentCount != null
            ? ` across ${data.documentCount.toLocaleString()} document${data.documentCount === 1 ? "" : "s"}`
            : null}
          .
        </p>
        <p className="text-xs">
          {data.totalCharacters.toLocaleString()} characters measured for pricing
          {data.includedCharacters != null
            ? ` (${data.includedCharacters.toLocaleString()} included)`
            : null}
          {data.billableCharacters != null && data.billableCharacters > 0
            ? ` · ${data.billableCharacters.toLocaleString()} billable above the included tier`
            : null}
        </p>
        {breakdown ? (
          <p>{formatPricingBreakdownLine(breakdown, data.quotedCurrency ?? "eur", data.billableCharacters)}</p>
        ) : null}
      </div>
    </details>
  );
}

const COVERAGE_BAND_LABELS: Record<string, string> = {
  covered: "Covered",
  partiallycovered: "Partially covered",
  notcovered: "Not covered",
};

const ITEM_TYPE_LABELS: Record<string, string> = {
  obligation: "Obligation",
  guidance: "Guidance",
};

function normalizeBand(value: string): string {
  return value.trim().toLowerCase().replace(/[_-]/g, "");
}

function formatClassification(value: string): string {
  const key = normalizeBand(value);
  return COVERAGE_BAND_LABELS[key] ?? ITEM_TYPE_LABELS[key] ?? value;
}

function bandColorClasses(value: string): string {
  switch (normalizeBand(value)) {
    case "covered":
      return "bg-green-100 text-green-700";
    case "partiallycovered":
      return "bg-amber-100 text-amber-700";
    case "notcovered":
      return "bg-red-100 text-red-700";
    case "obligation":
      return "bg-orange-100 text-orange-800";
    case "guidance":
      return "bg-blue-100 text-blue-800";
    default:
      return "bg-brand-100 text-brand-700";
  }
}

function previewItemAccentClasses(value?: string): string {
  switch (normalizeBand(value ?? "")) {
    case "obligation":
      return "border-l-4 border-l-orange-500";
    case "guidance":
      return "border-l-4 border-l-blue-600";
    default:
      return "";
  }
}

function PreviewFindings({ preview }: { preview?: StatusResponse["preview"] }) {
  if (!preview) return null;

  const countLabel = previewSampleCountLabel(preview);

  if (preview.sections?.length) {
    return (
      <div className="mt-8">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-muted">
          Preview sample{countLabel}
        </h2>
        <div className="mt-3 flex flex-col gap-6">
          {preview.sections.map((section, i) => (
            <PreviewSectionBlock key={i} section={section} />
          ))}
        </div>
      </div>
    );
  }

  if (!preview.items?.length) return null;

  return (
    <div className="mt-8">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-muted">
        Preview sample{countLabel}
      </h2>
      <ul className="mt-3 flex flex-col gap-3">
        {preview.items.map((item, i) => (
          <PreviewItemRow key={i} item={item} />
        ))}
      </ul>
    </div>
  );
}

function PreviewSectionBlock({ section }: { section: PreviewSection }) {
  return (
    <div>
      {section.heading ? (
        <h3 className="text-base font-semibold text-ink">{section.heading}</h3>
      ) : null}
      <ul className={`flex flex-col gap-3 ${section.heading ? "mt-2" : ""}`}>
        {section.items?.map((item, i) => (
          <PreviewItemRow key={i} item={item} showSection={false} />
        ))}
      </ul>
    </div>
  );
}

function truncate(text: string, max: number): string {
  const trimmed = text.trim();
  return trimmed.length <= max ? trimmed : `${trimmed.slice(0, max - 1)}…`;
}

function PreviewItemRow({ item, showSection = true }: { item: PreviewItem; showSection?: boolean }) {
  return (
    <li
      className={`rounded-xl border border-slate-200 bg-white p-4 text-sm text-ink shadow-sm ${previewItemAccentClasses(
        item.classification,
      )}`}
    >
      {showSection && item.section ? (
        <p className="text-xs font-medium uppercase text-ink-muted">{item.section}</p>
      ) : null}
      <p className="mt-1">{item.text ?? "-"}</p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {item.classification ? (
          <span
            className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${bandColorClasses(
              item.classification,
            )}`}
          >
            {formatClassification(item.classification)}
          </span>
        ) : null}
        {item.coverageScore != null ? (
          <span className="text-xs font-medium text-ink-muted">
            Score {Math.round(item.coverageScore)}/100
          </span>
        ) : null}
      </div>
      {item.rationale ? (
        <p className="mt-2 text-xs italic text-ink-muted">{truncate(item.rationale, 180)}</p>
      ) : null}
    </li>
  );
}

function Waiting({ message, data }: { message?: string; data?: StatusResponse }) {
  const [now, setNow] = useState(() => Date.now());

  // Work begins when the engine starts, not when the user submits. Showing time-since-engine-start
  // keeps the elapsed counter honest when the job spent time queued before a worker picked it up.
  const workStart = data?.engineStartedAt ?? data?.submittedAt;

  useEffect(() => {
    if (!workStart) return;
    const tick = () => setNow(Date.now());
    tick();
    const id = window.setInterval(tick, 30_000);
    return () => window.clearInterval(id);
  }, [workStart]);

  const elapsedMinutes = workStart
    ? Math.max(0, Math.floor((now - new Date(workStart).getTime()) / 60_000))
    : 0;

  // If there was a meaningful gap between submit and engine start, surface it as queue time
  // rather than letting it inflate the "working for" counter.
  const queuedMinutes =
    data?.engineStartedAt && data?.submittedAt
      ? Math.max(
          0,
          Math.floor(
            (new Date(data.engineStartedAt).getTime() - new Date(data.submittedAt).getTime()) / 60_000,
          ),
        )
      : 0;

  const stageHint = data ? processingStageHint(data, now) : null;
  const working = !!data?.engineStartedAt;
  const elapsedLabel =
    elapsedMinutes <= 0
      ? working
        ? "Working, just started"
        : "Just started"
      : `${working ? "Working for" : ""} ${elapsedMinutes} minute${elapsedMinutes === 1 ? "" : "s"}${working ? "" : " elapsed"}`.trim();

  const etaLabel = data && !isPastEstimatedCompletion(data, now) ? remainingEstimateLabel(data, now) : null;

  // Under backpressure the worker has not picked up the job yet.
  const queueLabel =
    !working && data?.queueDepth && data.queueDepth > 0
      ? data.queueBacklogged
        ? "Other jobs are in the queue ahead of yours (including work in progress)"
        : "Waiting for a worker to pick up your job"
      : null;

  const defaultMessage =
    "We're preparing your preview. This page updates automatically, and we'll email you when it's ready.";

  return (
    <div className="flex flex-col items-center py-12 text-center">
      <span className="mb-6 h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-brand-600" />
      <h1 className="text-xl font-bold text-ink sm:text-2xl">Preparing your preview</h1>
      {workStart ? (
        <p className="mt-2 text-sm font-medium text-brand-700">{elapsedLabel}</p>
      ) : null}
      {queueLabel ? (
        <p className="mt-1 text-sm font-medium text-brand-700">{queueLabel}</p>
      ) : null}
      {queuedMinutes >= 1 ? (
        <p className="mt-1 text-xs text-ink-muted">
          (Queued {queuedMinutes} minute{queuedMinutes === 1 ? "" : "s"} before starting)
        </p>
      ) : null}
      {etaLabel ? <p className="mt-1 text-sm text-ink-muted">{etaLabel}</p> : null}
      {stageHint ? <p className="mt-2 text-sm text-ink-muted">{stageHint}</p> : null}
      <p className="mt-3 max-w-md text-ink-soft">{message ?? defaultMessage}</p>
    </div>
  );
}

function resolveEstimatedCompletionMs(data: StatusResponse): number | null {
  if (data.estimatedCompletionAt) {
    const ms = new Date(data.estimatedCompletionAt).getTime();
    return Number.isNaN(ms) ? null : ms;
  }
  // Fall back to engine-start + duration when available so the countdown reflects actual work,
  // not the moment of submission (which may include queue time).
  const start = data.engineStartedAt ?? data.submittedAt;
  if (start && data.estimatedDurationSeconds != null) {
    const ms = new Date(start).getTime() + data.estimatedDurationSeconds * 1000;
    return Number.isNaN(ms) ? null : ms;
  }
  return null;
}

function isPastEstimatedCompletion(data: StatusResponse, now: number): boolean {
  const completionMs = resolveEstimatedCompletionMs(data);
  return completionMs != null && now >= completionMs;
}

/**
 * Rough "time remaining" range derived from the server ETA. Hides once the estimate
 * elapses so the stage hint can describe ongoing work (e.g. Word export).
 */
function remainingEstimateLabel(data: StatusResponse, now: number): string | null {
  const completionMs = resolveEstimatedCompletionMs(data);
  if (completionMs == null) return null;

  const remainingSec = (completionMs - now) / 1000;
  if (remainingSec <= 0) return null;

  const lowMin = Math.max(1, Math.floor((remainingSec * 0.85) / 60));
  const highMin = Math.max(lowMin, Math.ceil((remainingSec * 1.4) / 60));

  if (highMin <= 1) return "Estimated: about a minute remaining";
  if (lowMin === highMin) return `Estimated: about ${lowMin} minutes remaining`;
  return `Estimated: about ${lowMin} to ${highMin} minutes remaining`;
}

const PROCESSING_PHASE_LABELS: Record<string, string> = {
  queued: "Waiting to start…",
  parsing_documents: "Parsing your documents…",
  extracting_obligations: "Extracting obligations…",
  preparing_search: "Preparing search indexes…",
  running_coverage: "Running coverage analysis…",
  building_report: "Building your Word report…",
  finalizing_preview: "Preparing your preview…",
};

function processingStageHint(data: StatusResponse, now: number): string | null {
  if (data.processingPhase && PROCESSING_PHASE_LABELS[data.processingPhase]) {
    return PROCESSING_PHASE_LABELS[data.processingPhase];
  }

  const completionMs = resolveEstimatedCompletionMs(data);
  const overdueMs = completionMs != null ? now - completionMs : 0;

  if (completionMs != null && overdueMs > 0) {
    if (overdueMs > 3 * 60_000) {
      return "Still working. Larger reports can take a bit longer…";
    }
    return "Generating your report…";
  }

  if (data.engineGuids?.length) {
    return "Analysing document coverage…";
  }

  return "Upload received. Starting analysis…";
}
